import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';

interface FlipPanelProps {
  flipped: boolean;
  front: ReactNode;
  back: ReactNode;
  // Labels the faces for screen readers.
  frontLabel: string;
  backLabel: string;
}

// Measures whichever face is showing and reports its height, so the container
// can grow into the taller news face instead of clipping it.
const useFaceHeight = (
  ref: React.RefObject<HTMLDivElement>,
  active: boolean
) => {
  const [height, setHeight] = useState<number>();

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const measure = () => setHeight(node.offsetHeight);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
    // `active` is in the deps so the showing face re-measures on flip: its
    // images may only have loaded while it was hidden.
  }, [ref, active]);

  return height;
};

const FlipPanel = ({
  flipped,
  front,
  back,
  frontLabel,
  backLabel,
}: FlipPanelProps) => {
  const frontRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLDivElement>(null);

  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const frontHeight = useFaceHeight(frontRef, !flipped);
  const backHeight = useFaceHeight(backRef, flipped);
  const height = flipped ? backHeight : frontHeight;

  // Skip the opening transition so the panel doesn't animate up from 0 on load.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (height !== undefined) setReady(true);
  }, [height]);

  // Safari ignores backface-visibility on faces holding backdrop-filter glass,
  // so the turned-away face shows through mirrored. Hiding it outright, at
  // the flip's midpoint when both faces are edge-on, works everywhere.
  const swapDelay = reduceMotion ? '0s' : '0.3s';

  const faceSx = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    // Hiding the back of each face is what stops the two showing through each
    // other mid-rotation.
    backfaceVisibility: 'hidden' as const,
    WebkitBackfaceVisibility: 'hidden' as const,
    transition: ready
      ? `opacity 0.25s ease, visibility 0s linear ${swapDelay}`
      : 'none',
  };

  return (
    <Box
      sx={{
        position: 'relative',
        perspective: 1600,
        height: height ?? 'auto',
        transition: ready ? 'height 0.45s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          transformStyle: 'preserve-3d',
          transition: ready && !reduceMotion
            ? 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'none',
          transform: reduceMotion
            ? 'none'
            : `rotateY(${flipped ? 180 : 0}deg)`,
        }}
      >
        <Box
          ref={frontRef}
          aria-label={frontLabel}
          aria-hidden={flipped}
          // `inert` keeps Tab out of the hidden face; without it focus lands on
          // links nobody can see.
          {...(flipped ? { inert: '' } : {})}
          sx={{
            ...faceSx,
            position: flipped ? 'absolute' : 'relative',
            opacity: reduceMotion && flipped ? 0 : 1,
            visibility: flipped ? 'hidden' : 'visible',
            pointerEvents: flipped ? 'none' : 'auto',
          }}
        >
          {front}
        </Box>

        <Box
          ref={backRef}
          aria-label={backLabel}
          aria-hidden={!flipped}
          {...(flipped ? {} : { inert: '' })}
          sx={{
            ...faceSx,
            position: flipped ? 'relative' : 'absolute',
            transform: reduceMotion ? 'none' : 'rotateY(180deg)',
            opacity: reduceMotion && !flipped ? 0 : 1,
            visibility: flipped ? 'visible' : 'hidden',
            pointerEvents: flipped ? 'auto' : 'none',
          }}
        >
          {back}
        </Box>
      </Box>
    </Box>
  );
};

export default FlipPanel;
